import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui';
import { Icon } from '@/components/Icon';
import { ReportSheet } from '@/components/ReportSheet';
import { api, type FeedActivity } from '@/lib/api';
import { useBackHandler } from '@/hooks/useBackHandler';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { SheetGrip } from '@/components/ui';
import { useT } from '@/i18n/react';
import './activityCardMenu.css';

/**
 * Feed-kártya `⋯` menüje idegen szerzőnél (spec `docs/02`, kép #22):
 * Jelentés · Némítás · Követés vége · Tiltás.
 *
 * A „Követés vége" csak akkor jelenik meg, ha valóban követed — a kártya ezt
 * nem tudja, ezért a menü megnyitásakor egyszer lekérjük a kapcsolatot. Amíg
 * nem jött meg (vagy ha nem sikerült), a tétel egyszerűen nem látszik.
 *
 * Siker után a menü VISSZAIGAZOLÁSRA vált (ugyanaz az elv, mint a
 * `ReportSheet`-nél, Geri kérése, GRUNDO #62): a kártya eltűnése magában nem
 * mondja meg, mi történt és hol vonható vissza. A feed maga frissül — minden
 * nem-GET kérés után az `api.ts` érvényteleníti az `['activities']` kulcsot.
 */

type Done = { title: string; lead: string };

export function ActivityCardMenu({ item, onClose }: { item: FeedActivity; onClose: () => void }) {
  const t = useT();
  const { username } = item.author;
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<Done | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  /* Android vissza gomb: ez a réteg záródjon (a jelentőlap a sajátját kezeli). */
  useBackHandler(!reportOpen, () => onClose());

  useEffect(() => {
    let alive = true;
    api
      .publicProfile(username)
      .then((result) => {
        if (alive) setFollowing(result.relationship.following);
      })
      .catch(() => {
        /* Nem kritikus: a „Követés vége" ilyenkor nem jelenik meg. */
      });
    return () => {
      alive = false;
    };
  }, [username]);

  function act(action: () => Promise<unknown>, result: Done) {
    setBusy(true);
    setError('');
    action()
      .then(() => setDone(result))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : t.feed.menu.failed);
      })
      .finally(() => setBusy(false));
  }

  // Lefelé húzva is lecsukható (#71); a „kész" panelre váltáskor újraköt.
  const panelRef = useSwipeToClose<HTMLDivElement>(onClose, !reportOpen, done);

  if (reportOpen) {
    return (
      <ReportSheet
        target={{ kind: 'activity', activityId: item.id }}
        title={t.feed.menu.report}
        onClose={onClose}
        onDone={onClose}
      />
    );
  }

  // Portálban (DECISIONS #52): a feed a `PullToRefresh` transzformált rétegében ül.
  return createPortal(
    <div className="amenu" role="dialog" aria-label={t.feed.menu.dialogLabel}>
      <button type="button" className="amenu__scrim" aria-label={t.feed.menu.close} onClick={onClose} />
      {done ? (
        <div ref={panelRef} className="amenu__panel amenu__panel--done" role="status">
          <SheetGrip />
          <span className="amenu__done-icon">
            <Icon name="check" size={32} />
          </span>
          <span className="amenu__title">{done.title}</span>
          <p className="amenu__lead">{done.lead}</p>
          <Button block onClick={onClose}>
            {t.feed.menu.confirm}
          </Button>
        </div>
      ) : (
        <div ref={panelRef} className="amenu__panel">
          <SheetGrip />
          <span className="amenu__title">{t.feed.menu.options}</span>
          <button
            type="button"
            className="amenu__item amenu__item--warn"
            onClick={() => setReportOpen(true)}
          >
            {t.feed.menu.report}
          </button>
          <button
            type="button"
            className="amenu__item"
            disabled={busy}
            onClick={() =>
              act(() => api.muteUser(username), {
                title: t.feed.menu.mutedTitle(username),
                lead: t.feed.menu.mutedLead,
              })
            }
          >
            {t.feed.menu.mute(username)}
          </button>
          {following ? (
            <button
              type="button"
              className="amenu__item"
              disabled={busy}
              onClick={() =>
                act(() => api.unfollow(username), {
                  title: t.feed.menu.unfollowedTitle(username),
                  lead: t.feed.menu.unfollowedLead,
                })
              }
            >
              {t.feed.menu.unfollow}
            </button>
          ) : null}
          <button
            type="button"
            className="amenu__item amenu__item--danger"
            disabled={busy}
            onClick={() =>
              act(() => api.blockUser(username), {
                title: t.feed.menu.blockedTitle(username),
                lead: t.feed.menu.blockedLead,
              })
            }
          >
            {t.feed.menu.block(username)}
          </button>
          {error ? (
            <p className="field__error" role="alert">
              {error}
            </p>
          ) : null}
          <button type="button" className="amenu__item amenu__item--cancel" onClick={onClose}>
            {t.feed.menu.cancel}
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
